/**
 * An arbitrary-depth, drag-to-reparent tree over a FLAT list of anything.
 *
 * WHY THIS EXISTS. The only tree in the product was hard-wired to one screen:
 * it read a topic from context, fetched that topic's keywords, typed its nodes
 * as TopicKeyword, and rendered keyword-specific rows. The tree MECHANICS in
 * there were always depth-agnostic — depth is just a parent-id chain — but none
 * of it could be pointed at anything else, so the Subject workspace's node rail
 * would have meant a second copy of the same wiring. (That screen is gone now;
 * the rail and Link Builder's merged tree both run on this.)
 *
 * So the mechanics move here and the meaning stays with the caller: you supply
 * a flat list, say how to read an id/parent/label off an item, say how to
 * persist a re-parent, and render your own row. Nothing about keywords, topics
 * or nodes appears below.
 *
 * WHAT IT DELIBERATELY DOES NOT DO. Re-parenting only — no reordering within a
 * parent, because the underlying data has no order column and inventing one in
 * the UI would be a promise the server cannot keep. Order comes from `sort`.
 * Which is why `dropTargetOffset` is 0 and there is no drop placeholder: every
 * drop lands ON a row, never between two, so the gesture cannot suggest an
 * ordering that will not survive the refetch.
 * And it never moves optimistically: `onReparent` persists, the caller refetches,
 * and the tree re-renders from the returned data. The library hands back a
 * `_newTree` on drop; ignoring it is intentional — the server is the truth, and
 * an optimistic move that later fails leaves a tree that lies.
 */
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { Tree } from '@minoru/react-dnd-treeview';
import type { NodeModel, DropOptions, TreeMethods } from '@minoru/react-dnd-treeview';

/** What a caller can drive from outside: expand and collapse the whole tree. */
export type { TreeMethods };

// Re-exported so callers need one import; it lives in its own dependency-free
// module because this file cannot be loaded under CRA's jest (react-dnd is ESM).
export { collectDescendantIds } from './treeHelpers';

/** What the library gives a row when it renders it. */
export interface TreeRowContext {
  depth: number;
  isOpen: boolean;
  hasChild: boolean;
  onToggle: () => void;
  /** Attach to whatever should be the drag handle. */
  handleRef: React.Ref<any>;
  /** True while this row is the row a drag would land on. */
  isDropTarget: boolean;
  isDragging: boolean;
}

export interface DraggableTreeProps<T> {
  items: T[];
  getId: (item: T) => number;
  /** null / undefined / 0 all mean "top level". */
  getParentId: (item: T) => number | null | undefined;
  /** Used by the library for its own text handling; rows render via renderRow. */
  getLabel: (item: T) => string;
  /** Must return an element — the tree library's render slot is not nullable. */
  renderRow: (item: T, ctx: TreeRowContext) => React.ReactElement;
  /**
   * Persist a move. `newParentId` is null when dropped at the top level.
   * Throwing surfaces through onError and the tree stays where it was.
   */
  /**
   * Persist a drop. `newParentId` is null when dropped at the top level.
   *
   * `point` is where on screen the drop happened, so a caller that has to ask
   * a question about the drop can put the question where the user is looking
   * rather than in a corner of the page. Null when react-dnd has no offset.
   */
  onReparent: (
    id: number,
    newParentId: number | null,
    ctx: { point: { x: number; y: number } | null },
  ) => Promise<void> | void;
  onError?: (message: string) => void;
  /** Sibling order. Defaults to label, ascending. */
  sort?: (a: T, b: T) => number;
  /**
   * Ids to start expanded, instead of expanding everything.
   *
   * Needed because the tree usually mounts BEFORE its data arrives — so a
   * blanket `initialOpen` applies to an empty list and every node shows up
   * closed. Pass the ancestors of whatever should be visible; the tree is keyed
   * on the arrival of data so this applies once the rows are actually there.
   *
   * After that, a new list only OPENS more branches; it never closes any.
   * Handed straight to the library, every new list replaced its open set, so
   * clicking a row closed every branch except the path to that row.
   */
  openIds?: number[];
  /**
   * Receives the tree's imperative handle once it mounts.
   *
   * Open/closed state belongs to the tree library, so "expand all" cannot be a
   * prop — there is nothing on this side to set. The caller keeps the handle
   * and calls `openAll()` / `closeAll()` on it.
   */
  onReady?: (methods: TreeMethods | null) => void;
  className?: string;
}

export default function DraggableTree<T>({
  items,
  getId,
  getParentId,
  getLabel,
  renderRow,
  onReparent,
  onError,
  sort,
  openIds,
  onReady,
  className = 'space-y-0.5',
}: DraggableTreeProps<T>) {
  // The tree remounts when data first arrives (see `key` below), which throws
  // the old handle away — so the callback fires again with the new one rather
  // than the caller holding a stale reference to a dead instance.
  const methodsRef = useRef<TreeMethods | null>(null);
  const handleRef = useCallback(
    (methods: TreeMethods | null) => {
      methodsRef.current = methods;
      onReady?.(methods);
    },
    [onReady],
  );
  const treeData: NodeModel<T>[] = useMemo(
    () =>
      items.map((item) => ({
        id: getId(item),
        // The library uses 0 for the root; null and undefined both mean the same
        // thing in the data, so normalize here rather than at every call site.
        parent: getParentId(item) || 0,
        text: getLabel(item),
        droppable: true,
        data: item,
      })),
    [items, getId, getParentId, getLabel],
  );

  // The library re-applies `initialOpen` whenever it changes, replacing what
  // the user has opened. So it gets the list as it stood when the rows
  // arrived, and nothing after; later lists go through `open()`, which adds.
  const ready = treeData.length > 0;
  const openIdsRef = useRef(openIds);
  openIdsRef.current = openIds;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const initialOpen = useMemo(() => openIdsRef.current ?? true, [ready]);
  const openKey = (openIds ?? []).join(',');
  useEffect(() => {
    if (openIds?.length) methodsRef.current?.open(openIds);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openKey]);

  const handleDrop = useCallback(
    async (_newTree: NodeModel<T>[], options: DropOptions<T>) => {
      const { dragSourceId, dropTargetId, monitor } = options;
      if (dragSourceId === undefined) return;
      const newParentId = dropTargetId === 0 ? null : Number(dropTargetId);
      // Captured now, not read later: react-dnd tears the monitor's offsets
      // down as soon as the drag ends, so anything that awaits first gets null.
      const offset = monitor?.getClientOffset?.() ?? null;
      const point = offset ? { x: offset.x, y: offset.y } : null;
      try {
        await onReparent(Number(dragSourceId), newParentId, { point });
      } catch (err) {
        onError?.(err instanceof Error ? err.message : 'Failed to update parent');
      }
    },
    [onReparent, onError],
  );

  return (
    <DndProvider backend={HTML5Backend}>
      <Tree<T>
        ref={handleRef}
        tree={treeData}
        rootId={0}
        onDrop={handleDrop}
        sort={(a, b) => {
          if (sort && a.data && b.data) return sort(a.data, b.data);
          return a.text.localeCompare(b.text);
        }}
        // Remount once the rows land, so initialOpen has something to open.
        key={ready ? 'ready' : 'empty'}
        initialOpen={initialOpen}
        insertDroppableFirst={false}
        // Zero, so every drop onto a row lands ON that row.
        //
        // A non-zero offset carves strips off the top and bottom of each row
        // where a drop means "between these two siblings" instead. That is a
        // reordering gesture, and this tree has nothing to reorder — order comes
        // from a comparator and is never persisted, so the library discards the
        // index and files the drop under the row's PARENT. Two visibly different
        // gestures, one indistinguishable payload: `destinationIndex` is only
        // populated when `sort` is false, which it never is here.
        //
        // That mattered little while a drop meant one thing. It matters now that
        // a caller may need to ask what the drop meant, because "between two
        // rows" is not a question anyone can answer.
        dropTargetOffset={0}
        classes={{
          root: className,
          listItem: 'list-none',
          dropTarget: 'bg-purple-50 dark:bg-purple-900/30 rounded',
          draggingSource: 'opacity-30',
        }}
        render={(node, ctx) => renderRow(node.data as T, ctx as TreeRowContext)}
      />
    </DndProvider>
  );
}
